import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent,
  Paper,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApiGet, useApiSend } from '../../utils/httpClientTs';
import {
  getDocumentEditList,
  getCategories,
  getSubCategories,
  createDocumentVersion,
} from '../../endpoints/documents';
import {
  DocumentList,
  DocumentVersionCreate,
  PaginatedDocumentResponse,
} from '../../types/document';

// Local interface for the extended document data returned by getDocumentEditList
interface DocumentWithMetadata extends DocumentList {
  categories: string[];
  sub_categories: string[];
  tags: string[];
  active_versions: string[];
}

const DocumentManager: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedSubCategories, setSelectedSubCategories] = useState<string[]>(
    []
  );
  const [showFilters, setShowFilters] = useState(false);
  const [showAddVersionModal, setShowAddVersionModal] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string>('');
  const [newVersionData, setNewVersionData] = useState<DocumentVersionCreate>({
    version: '',
    description: '',
  });

  // Fetch categories and sub-categories
  const { data: categories = [] } = useApiGet(['categories'], getCategories);
  const { data: subCategories = [] } = useApiGet(
    ['sub-categories'],
    getSubCategories
  );

  // Filter sub-categories based on selected categories
  const filteredSubCategories = subCategories.filter((subCat) =>
    selectedCategories.includes(subCat.category_id)
  );

  // API hook for creating document versions
  const { mutate: createVersion, isPending: isCreatingVersion } = useApiSend(
    (params: { documentId: string; data: DocumentVersionCreate }) =>
      createDocumentVersion(params.documentId, params.data),
    undefined,
    undefined,
    [['documents-edit']] // Invalidate the documents list after creating a version
  );

  // Fetch documents with filters
  const {
    data: documentsResponse,
    isLoading,
    refetch,
  } = useApiGet<
    PaginatedDocumentResponse & {
      data: DocumentWithMetadata[];
    }
  >(
    [
      'documents-edit',
      searchTerm || '',
      selectedCategories.length > 0
        ? `cat_${selectedCategories.join('_')}`
        : 'cat_none',
      selectedSubCategories.length > 0
        ? `sub_${selectedSubCategories.join('_')}`
        : 'sub_none',
    ],
    () =>
      getDocumentEditList({
        search: searchTerm || undefined,
        categories:
          selectedCategories.length > 0 ? selectedCategories : undefined,
        sub_categories:
          selectedSubCategories.length > 0 ? selectedSubCategories : undefined,
      }),
    { enabled: true }
  );

  // Extract documents from paginated response
  const documents: DocumentWithMetadata[] = documentsResponse?.data || [];

  const handleSearch = () => {
    refetch();
  };

  const handleCategoryChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const newCategories = typeof value === 'string' ? value.split(',') : value;
    setSelectedCategories(newCategories);

    // Clear sub-categories that don't belong to the new selected categories
    if (newCategories.length === 0) {
      setSelectedSubCategories([]);
    } else {
      setSelectedSubCategories((prev) =>
        prev.filter((subCatId) => {
          const subCat = subCategories.find((sc) => sc.id === subCatId);
          return subCat && newCategories.includes(subCat.category_id);
        })
      );
    }
  };

  const handleSubCategoryChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedSubCategories(
      typeof value === 'string' ? value.split(',') : value
    );
  };

  const removeCategory = (categoryId: string) => {
    const newCategories = selectedCategories.filter((id) => id !== categoryId);
    setSelectedCategories(newCategories);

    // Also remove sub-categories that don't belong to remaining categories
    const remainingSubCategories = selectedSubCategories.filter((subCatId) => {
      const subCat = subCategories.find((sc) => sc.id === subCatId);
      return subCat && newCategories.includes(subCat.category_id);
    });
    setSelectedSubCategories(remainingSubCategories);
  };

  const removeSubCategory = (subCategoryId: string) => {
    const newSubCategories = selectedSubCategories.filter(
      (id) => id !== subCategoryId
    );
    setSelectedSubCategories(newSubCategories);
  };

  const handleCreateDocument = () => {
    navigate('/library/create');
  };

  const handleOpenAddVersionModal = (documentId: string) => {
    setSelectedDocumentId(documentId);
    setNewVersionData({ version: '', description: '' });
    setShowAddVersionModal(true);
  };

  const handleCloseAddVersionModal = () => {
    setShowAddVersionModal(false);
    setSelectedDocumentId('');
    setNewVersionData({ version: '', description: '' });
  };

  const handleCreateVersion = () => {
    console.log(newVersionData);
    if (newVersionData.version.trim()) {
      createVersion({
        documentId: selectedDocumentId,
        data: newVersionData,
      });
      handleCloseAddVersionModal();
    }
    console.log(newVersionData);
  };

  const handleVersionInputChange = (
    field: keyof DocumentVersionCreate,
    value: string
  ) => {
    setNewVersionData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategories([]);
    setSelectedSubCategories([]);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          Document Manager
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateDocument}
          sx={{
            backgroundColor: '#00A859',
            '&:hover': { backgroundColor: '#008a4a' },
          }}
        >
          Create Document
        </Button>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant={showFilters ? 'contained' : 'outlined'}
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
              sx={{
                backgroundColor: showFilters ? '#0072CE' : 'transparent',
                color: showFilters ? '#fff' : '#0072CE',
                borderColor: '#0072CE',
                '&:hover': {
                  backgroundColor: showFilters
                    ? '#005a9e'
                    : 'rgba(0, 114, 206, 0.04)',
                },
              }}
            >
              Filters
            </Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="outlined" onClick={handleSearch}>
              Search
            </Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="outlined" onClick={clearFilters}>
              Clear
            </Button>
          </Grid>
        </Grid>

        {/* Advanced Filters */}
        {showFilters && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Categories</InputLabel>
                  <Select
                    multiple
                    value={selectedCategories}
                    onChange={handleCategoryChange}
                    input={<OutlinedInput label="Categories" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const category = categories.find(
                            (cat) => cat.id === value
                          );
                          return (
                            <Chip
                              key={value}
                              label={category?.name || value}
                              size="small"
                              onDelete={() => removeCategory(value)}
                              deleteIcon={<CloseIcon />}
                              sx={{
                                '& .MuiChip-deleteIcon': {
                                  color: '#666',
                                  '&:hover': {
                                    color: '#333',
                                  },
                                },
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {filteredSubCategories.length > 0 && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Sub-Categories</InputLabel>
                    <Select
                      multiple
                      value={selectedSubCategories}
                      onChange={handleSubCategoryChange}
                      input={<OutlinedInput label="Sub-Categories" />}
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                        >
                          {selected.map((value) => {
                            const subCategory = subCategories.find(
                              (subCat) => subCat.id === value
                            );
                            return (
                              <Chip
                                key={value}
                                label={subCategory?.name || value}
                                size="small"
                                onDelete={() => removeSubCategory(value)}
                                deleteIcon={<CloseIcon />}
                                sx={{
                                  '& .MuiChip-deleteIcon': {
                                    color: '#666',
                                    '&:hover': {
                                      color: '#333',
                                    },
                                  },
                                }}
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {filteredSubCategories.map((subCategory) => (
                        <MenuItem key={subCategory.id} value={subCategory.id}>
                          {subCategory.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Documents List */}
      <Grid container spacing={2}>
        {isLoading ? (
          <Grid item xs={12}>
            <Typography>Loading documents...</Typography>
          </Grid>
        ) : documents.length === 0 ? (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No documents found
              </Typography>
              <Typography color="text.secondary">
                {searchTerm ||
                selectedCategories.length > 0 ||
                selectedSubCategories.length > 0
                  ? 'Try adjusting your search criteria or filters.'
                  : 'Create your first document to get started.'}
              </Typography>
            </Paper>
          </Grid>
        ) : (
          documents.map((document) => (
            <Grid item xs={12} md={6} lg={4} key={document.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" component="h3">
                      {document.name}
                    </Typography>
                  </Box>

                  <Stack spacing={1} sx={{ mb: 2 }}>
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Active Versions:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {document.active_versions.map((version) => (
                          <Chip
                            key={version}
                            label={version}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>

                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Categories:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {document.categories.map((category) => (
                          <Chip
                            key={category}
                            label={category}
                            size="small"
                            color="primary"
                          />
                        ))}
                      </Box>
                    </Box>

                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Sub-Categories:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {document.sub_categories.map((subCategory) => (
                          <Chip
                            key={subCategory}
                            label={subCategory}
                            size="small"
                            color="secondary"
                          />
                        ))}
                      </Box>
                    </Box>

                    {document.tags.length > 0 && (
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Tags:
                        </Typography>
                        <Box
                          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                        >
                          {document.tags.map((tag) => (
                            <Chip
                              key={tag}
                              label={tag}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Stack>

                  {/* Add Version Button */}
                  <Box sx={{ mt: 'auto', pt: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      onClick={() => handleOpenAddVersionModal(document.id)}
                      disabled={isCreatingVersion}
                      sx={{
                        color: '#00A859',
                        borderColor: '#00A859',
                        '&:hover': {
                          borderColor: '#008a4a',
                          backgroundColor: 'rgba(0, 168, 89, 0.04)',
                        },
                      }}
                    >
                      Add New Version
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      {/* Add New Version Modal */}
      <Dialog
        open={showAddVersionModal}
        onClose={handleCloseAddVersionModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Version</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Version"
              value={newVersionData.version}
              onChange={(e) =>
                handleVersionInputChange('version', e.target.value)
              }
              size="small"
              sx={{ mb: 2 }}
              placeholder="e.g., 1.1, 2.0, etc."
              required
            />
            <TextField
              fullWidth
              label="Description"
              value={newVersionData.description}
              onChange={(e) =>
                handleVersionInputChange('description', e.target.value)
              }
              size="small"
              multiline
              rows={3}
              placeholder="Describe what's new in this version..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseAddVersionModal}
            disabled={isCreatingVersion}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateVersion}
            variant="contained"
            disabled={!newVersionData.version.trim() || isCreatingVersion}
            sx={{
              backgroundColor: '#00A859',
              '&:hover': { backgroundColor: '#008a4a' },
            }}
          >
            {isCreatingVersion ? 'Creating...' : 'Create Version'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentManager;
